/**
 * sttPollAdapter - Normalizes transcription job status into PollUpdate
 * consumed by GenerationContext.
 *
 * STT uses the generic /api/v1/jobs/{id} endpoint (serverType: 'generation').
 * The full transcription result lives in extra_metadata on the job record.
 *
 * Polling timing note: RETRY_CONFIGS.TRANSCRIPTION specifies 150 attempts at
 * 2 000 ms each. GenerationContext.startPoller() currently uses fixed defaults
 * (LONG_RUNNING: 3 600 attempts at 1 000 ms), which covers that window. The
 * intervalMs and maxAttempts fields on StartJobInput can be passed by the
 * calling hook to apply the TRANSCRIPTION config once the context supports
 * per-job overrides natively (already in the StartJobInput type, wired to the
 * poller). Until then, the wider LONG_RUNNING window keeps behavior safe.
 *
 * Self-registers on module load so GenerationContext can resume polling
 * persisted transcription jobs after a reload.
 */

import { getAuthHeadersFromStorage } from '../../utils/httpUtils';
import { VideoGenerationAPI } from '../api';
import type { TranscriptionSegment } from '../transcription';
import type { PollAdapter, PollUpdate } from '../../types/generationJob';
import { sanitizeBackendError } from '../../utils/errorSanitizer';
import { registerPollAdapter } from './pollAdapterRegistry';

/** Shape stored in GenerationJob.result for completed transcription jobs. */
export interface SttJobResult {
  segments: TranscriptionSegment[];
  fullText: string;
  originalText?: string;
  detectedLanguage: string;
  processingTimeSeconds: number;
  segmentCount?: number;
  aiEnhanced?: boolean;
  refinementApplied?: boolean;
  modelUsed?: string;
}

interface SttJobStatusData {
  job_id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress?: number;
  message?: string;
  error?: string;
  extra_metadata?: {
    segments?: TranscriptionSegment[];
    full_text?: string;
    original_text?: string;
    detected_language?: string;
    segment_count?: number;
    ai_enhanced?: boolean;
    refinement_applied?: boolean;
    model_used?: string;
    processing_time_seconds?: number;
  };
}

interface SttStatusEnvelope {
  success: boolean;
  data?: SttJobStatusData;
  error?: string;
}

export const sttPollAdapter: PollAdapter = async (
  jobId: string
): Promise<PollUpdate<SttJobResult>> => {
  const baseUrl = await VideoGenerationAPI.getBaseUrl();
  const headers = await getAuthHeadersFromStorage();

  let envelope: SttStatusEnvelope;
  try {
    const response = await fetch(`${baseUrl}/api/v1/jobs/${jobId}`, { headers });
    envelope = (await response.json()) as SttStatusEnvelope;
    if (!response.ok || !envelope.success) {
      return {
        done: true,
        status: 'failed',
        error: {
          message: envelope.error ?? 'Failed to get transcription status',
          sanitized: sanitizeBackendError(envelope),
        },
      };
    }
  } catch (err) {
    return {
      done: true,
      status: 'failed',
      error: {
        message: err instanceof Error ? err.message : 'Network error',
        sanitized: sanitizeBackendError(null),
      },
    };
  }

  const jobData = envelope.data;
  if (!jobData) {
    return {
      done: true,
      status: 'failed',
      error: {
        message: 'Invalid job status response',
        sanitized: sanitizeBackendError(envelope),
      },
    };
  }

  if (jobData.status === 'failed') {
    return {
      done: true,
      status: 'failed',
      error: {
        message: jobData.error ?? 'Transcription job failed',
        sanitized: sanitizeBackendError(jobData),
      },
    };
  }

  if (jobData.status === 'completed') {
    const meta = jobData.extra_metadata ?? {};
    if (!meta.full_text || !meta.segments) {
      return {
        done: true,
        status: 'failed',
        error: {
          message: 'Transcription completed but result data is missing',
          sanitized: sanitizeBackendError(jobData),
        },
      };
    }
    const result: SttJobResult = {
      segments: meta.segments,
      fullText: meta.full_text,
      originalText: meta.original_text,
      detectedLanguage: meta.detected_language ?? 'unknown',
      processingTimeSeconds: meta.processing_time_seconds ?? 0,
      segmentCount: meta.segment_count,
      aiEnhanced: meta.ai_enhanced,
      refinementApplied: meta.refinement_applied,
      modelUsed: meta.model_used,
    };
    return {
      done: true,
      status: 'completed',
      progress: 100,
      stage: 'completed',
      result,
    };
  }

  // pending or processing
  return {
    done: false,
    status: 'running',
    progress: typeof jobData.progress === 'number' ? jobData.progress : undefined,
    stage: jobData.status,
    message: jobData.message ?? undefined,
  };
};

registerPollAdapter('stt', sttPollAdapter);
